import axios, { AxiosError } from 'axios';
import pathfinder from '@fiverr-private/pathfinder';
import { toFormData } from '../../utils/toFormData';
import { logError } from '../../utils/logError';
import { formatErrorKeys } from '../formatErrorKeys';
import { formatSignupErrors } from '../formatSignupErrors';
import { isAxiosError } from '../../utils/isAxiosError';
import { SignUpPayload, SignUpResponse } from './signUp.types';
import { TOO_MANY_REQUESTS_STATUS } from './signUp.constants';

const processErrorKeys = (data: SignUpResponse) => {
    if (data.errorKeys) {
        return formatErrorKeys(data.errorKeys);
    }

    if (data.error_keys) {
        return formatSignupErrors(data.error_keys);
    }

    return null;
};

export const signUp = async ({ email, password, username, firstName, lastName, funnel }: SignUpPayload) => {
    try {
        const url = pathfinder('users');

        const formData = toFormData({
            'user[email]': email,
            'user[password]': password,
            'user[username]': username,
            ...(firstName ? { 'user[firstName]': firstName } : {}),
            ...(lastName ? { 'user[lastName]': lastName } : {}),
            funnel,
        });

        const { data } = await axios.post<SignUpResponse>(url, formData);

        const errorKeys = processErrorKeys(data);

        if (errorKeys && 'unknown' in errorKeys && errorKeys.unknown?.length) {
            logError({
                message: 'Identification Core SignUp: Unknown error keys',
                enrichment: {
                    errorKeys: errorKeys.unknown,
                },
            });
        }

        return {
            redirectUrl: data.redirect_url,
            errorKeys,
            externalId: data.external_id,
        };
    } catch (error) {
        if (isAxiosError(error)) {
            const axiosError = error as AxiosError<SignUpResponse>;

            if (axiosError.response?.status === TOO_MANY_REQUESTS_STATUS) {
                return {
                    errorKeys: {
                        username: ['sign_up_rate_limit'],
                    } as const,
                };
            }

            const errorKeys = axiosError.response?.data ? processErrorKeys(axiosError.response?.data) : null;

            if (errorKeys && 'unknown' in errorKeys && errorKeys.unknown?.length) {
                logError({
                    message: 'Unknown error keys',
                    enrichment: {
                        errorKeys: errorKeys.unknown,
                    },
                });
            }

            return { errorKeys };
        }

        logError({
            message: 'Error during sign up',
            error,
        });

        throw error;
    }
};
